import { EmojiRenderer } from "@circle-react-shared/EmojiRenderer";
import { shouldRenderLockIcon } from "@circle-react/components/Spaces/utils";
import { isIntendedOnNewTab } from "@circle-react/helpers/browserHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useResetSpaceSidebarNotifications } from "@circle-react/hooks/useSpaceSidebarNotifications";
import type { Appearance } from "@circle-react/providers/Theme/themes/constants";
import type { Space } from "@circle-react/types";
import { Icon } from "@circleco/compass/components/Icon";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link, useMatchRoute } from "@tanstack/react-router";

import { spaces } from "@/react/helpers/urlHelpers";

import { SidebarItem } from "../SidebarItem";
import { SidebarBadge } from "./SidebarBadge";
import { useSpaceUnreadState } from "./useSpaceUnreadState";

export interface SpaceListItemProps {
  currentAppearance: Appearance;
  isAdmin: boolean;
  isLargeScreen: boolean;
  space: Partial<Space>;
}

export const SpaceListItem = ({
  currentAppearance,
  isAdmin,
  isLargeScreen,
  space,
}: SpaceListItemProps) => {
  const buttonLink = spaces.update({ spaceSlug: space.slug ?? "" });
  const matchRoute = useMatchRoute();
  const isActive = Boolean(
    buttonLink && matchRoute({ to: buttonLink, fuzzy: true }),
  );
  const shouldDisableDragAndDrop = !isAdmin || !isLargeScreen;

  const { badgeCount, hasNotifications, hasUnreadContent } =
    useSpaceUnreadState(space.id);

  const { resetUnreadCount } = useResetSpaceSidebarNotifications(space.id);

  const shouldUseLockIcon = Boolean(
    shouldRenderLockIcon({
      shouldShowLockIconForNonMembers:
        space.show_lock_icon_for_non_members && !space.is_space_member,
      isPrivate: space.is_private,
      isAdmin,
    }),
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `space-${space.id}`,
    disabled: shouldDisableDragAndDrop,
    data: {
      type: "space",
      id: space.id,
      spaceGroupId: space.space_group_id,
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames({
        "pointer-events-none opacity-50": isDragging,
      })}
      draggable="false"
      {...attributes}
      {...listeners}
      tabIndex={-1}
    >
      <SidebarItem
        hasUnreadContent={hasUnreadContent}
        isActive={isActive}
        onClick={event => {
          if (isIntendedOnNewTab(event)) {
            void resetUnreadCount();
          }
        }}
        rootElement={
          <Link draggable="false" data-id={space.id} to={buttonLink} />
        }
      >
        <SidebarItem.Content
          endContent={
            (hasUnreadContent || hasNotifications) && (
              <SidebarBadge
                count={badgeCount}
                variant={hasNotifications ? "branded" : "secondary"}
              />
            )
          }
          label={space.name ?? ""}
          labelTestId="space-name"
          startContent={
            <EmojiRenderer
              appearance={currentAppearance}
              customEmojiDarkUrl={space.custom_emoji_dark_url}
              customEmojiUrl={space.custom_emoji_url}
              emoji={space.emoji}
              emojiClassName="text-sm justify-center"
              className="flex !h-4 !max-h-4 !w-4 max-w-none items-center leading-none"
              lockIcon={<Icon name="lock" size="sm" />}
              shouldUseLockIcon={shouldUseLockIcon}
            />
          }
        />
      </SidebarItem>
    </div>
  );
};
